import { makeObservable, observable, action, computed, runInAction, autorun, toJS, isObservable } from '../../../common/stores/mobx-config.js';
import { infoWindowDisplayManager } from '../managers/infoWindowDisplayManager.js';
import { storeEvents, EVENTS } from '../../../common/stores/storeEvents.js';
import { normalizeGeoString } from '@fbracq/read-gedcom-geneafan/src/utils/geoUtils.js';
import { UNIFIED_EVENT_TYPE_DECOMPRESSION } from '@fbracq/read-gedcom-geneafan/src/compression/eventCompression.js';
import { TownStatisticsManager } from '../../../gedcom/stores/townStatisticsManager.js';
import { infoWindowContentManager } from '../managers/infoWindowContentManager.js';
import { layerManager } from '../managers/layerManager.js';
import BaseLayerStore, { createClusterBadge } from './baseLayerStore.js';
import { getCoordinatesForTown } from '../../../utils/coordinatesLookup.js';
import { logger } from '../../../utils/debug/logger.js';
import { getName, getSurname, getGender } from '../../../gedcom/formatters/individualFormatters.js';

/**
 * Manage the display of all towns mentioned in the GEDCOM file
 * 
 * Key responsibilities:
 * - Manages all towns from GEDCOM file
 * - Processes all types of genealogical events (births, marriages, deaths)
 * - Provides a global context layer for the map
 * - Handles geocoding and marker management
 * - Manages caching and data persistence
 */
class FamilyTownsStore extends BaseLayerStore {
    constructor() {
        super('family');
        // Définir explicitement le nom de la couche de marqueurs
        this.markerLayerName = 'familyTowns';

        // Primary data storage
        this.eventsData = new Map();

        // Caching systems for performance optimization
        this.markerConfigs = new Map();               // Stores marker configurations by town
        this.infoWindowContentCache = new Map();      // Caches info window HTML content

        // Primary town data storage
        this.townsData = new Map();

        // State management
        this.isLoading = false;

        // Import context optimization
        this.isPendingMapProcessing = false; // Flag pour traitement différé de la map
        this.googleMapManager = null; // Référence vers GoogleMapManager quand il se charge

        // BaseLayerStore uses this.disposers (a Set) for its LAYERS.CHANGED
        // subscription — DON'T overwrite it (that orphaned the base disposer and
        // leaked the listener). Keep this store's keyed disposers separate.
        this.eventDisposers = new Map();

        // Propriétés pour le centrage optimisé
        this.calculatedBounds = null;         // Les limites calculées une seule fois
        this.hasTooManyMarkers = false;       // Indique si la carte contient trop de marqueurs espacés
        this.userIsNavigating = false;        // Indique si l'utilisateur navigue manuellement
        this.lastContainerDimensions = null;  // Mémorise les dimensions du conteneur lors du dernier calcul

        // Observable event collections by type
        this.events = observable({
            birth: observable([]),
            death: observable([]),
            marriage: observable([]),
            burial: observable([]),
            occupation: observable([]),
            event: observable([])
        });

        // MobX configuration
        makeObservable(this, {
            townsData: observable,
            isLoading: observable,
            events: observable,
            updateTownsViaProxy: action,
            setIsLoading: action,
            setTownsData: action,
            addOrUpdateTown: action,
            updateTown: action,
            toggleVisibility: action,
            clearAllTowns: action,
            finalizeAllTownsData: action,
            recalculateAllTownsStatistics: action,
            totalTowns: computed
        });

        // Automatic marker updates when data changes.
        // Keep the disposer so cleanup() (BaseLayerStore) can stop the autorun —
        // otherwise it survives a second GEDCOM load and fires on stale state.
        this.disposers.add(autorun(() => {
            // Debug logs désactivés pour éviter la pollution de la console
            // logger.map.familyTowns.debug(`🔍 FamilyTownsStore autorun check:`, {
            //     hasMap: !!this.map,
            //     townsDataSize: this.townsData.size,
            //     isLayerVisible: layerManager.isLayerVisible('family'),
            //     condition: !!(this.map && (this.townsData.size > 0 || layerManager.isLayerVisible('family')))
            // });
            
            if (this.map && (this.townsData.size > 0 || layerManager.isLayerVisible('family'))) {
                // logger.map.familyTowns.debug('✅ Autorun conditions met, calling updateMarkers');
                this.updateMarkers();
            } else {
                // logger.map.familyTowns.debug('❌ Autorun conditions not met, skipping updateMarkers');
            }
        }));

        this.setupEventSubscriptions();
    }

    /**
     * Sets up event listeners for GEDCOM data processing
     * Handles bulk individual additions, cache events, and cleanup
     */
    setupEventSubscriptions() {
        // Handle bulk addition of individuals
        const bulkIndividualsDisposer = storeEvents.subscribe(
            EVENTS.INDIVIDUALS.BULK_ADDED,

            (individuals) => {
                // logger.map.familyTowns.debug('👥 BULK_ADDED reçu avec', individuals.length, 'individus');
                // logger.map.familyTowns.debug('📊 FamilyTownsStore état avant BULK_ADDED: townsData size =', this.townsData.size);
                // console.time('processAllTownsEvents');

                runInAction(() => {
                    const townUpdates = new Map();
                    logger.map.familyTowns.time('processIndividuals');

                    const validEventTypes = ['birth', 'death', 'marriage'];

                    // Process each individual's events
                    let individualCount = 0;
                    let eventsCount = 0;
                    individuals.forEach(([id, individual]) => {
                        individualCount++;
                        // Log désactivé - trop verbeux pour le debug normal
                        // if (individualCount <= 3) {
                        //     logger.map.familyTowns.debug(`🔍 Individu ${individualCount}:`, JSON.stringify({
                        //         id: individual.i,
                        //         name: individual.name,
                        //         surname: individual.surname,
                        //         allProps: Object.keys(individual),
                        //         individualEvents: individual.individualEvents,
                        //         birthDate: individual.birthDate,
                        //         deathDate: individual.deathDate,
                        //         birthTownKey: individual.birthTownKey,
                        //         deathTownKey: individual.deathTownKey
                        //     }, null, 2));
                        // }
                        
                        // Phase 10: Utiliser le champ compressé 'e' au lieu de 'individualEvents'
                        if (individual.e) {
                            eventsCount += individual.e.length;
                        }
                        
                        individual.e?.forEach(compressedEvent => {
                            // Décompresser l'événement (Phase 6 Cloud)
                            const event = this.decompressEvent(compressedEvent);
                            
                            if (individualCount <= 2) {
                                logger.map.familyTowns.perf(`🔍 Décompression: ${JSON.stringify(compressedEvent)} -> ${JSON.stringify(event)}`);
                            }
                            
                            if (!event || !validEventTypes.includes(event.type)) return;
                            if (!event.town) {
                                if (individualCount <= 2) {
                                    logger.map.familyTowns.debug(`🔍 Événement ignoré (pas de town):`, event);
                                }
                                return;
                            }

                            const normalizedTownName = normalizeGeoString(event.town);
                            if (normalizedTownName === 'lieu_inconnu') return;

                            // Initialize town data if needed
                            if (!townUpdates.has(normalizedTownName)) {
                                // Vérifier si on a déjà les données de ville depuis le cache restauré
                                const existingTownData = this.townsData.get(normalizedTownName);
                                
                                if (existingTownData) {
                                    // Utiliser les données existantes du cache (avec noms originaux)
                                    // logger.map.familyTowns.debug(`✅ BULK_ADDED: Utilisation données cache pour "${normalizedTownName}" -> "${existingTownData.town}"`);
                                    townUpdates.set(normalizedTownName, {
                                        townData: {
                                            town: existingTownData.town,
                                            townDisplay: existingTownData.townDisplay,
                                            departement: existingTownData.departement,
                                            latitude: existingTownData.latitude || '',
                                            longitude: existingTownData.longitude || ''
                                        },
                                        events: []
                                    });
                                } else {
                                    // Fallback: créer avec les données disponibles
                                    const coordinates = getCoordinatesForTown(normalizedTownName);
                                    townUpdates.set(normalizedTownName, {
                                        townData: {
                                            town: event.town,
                                            townDisplay: event.town,
                                            latitude: coordinates?.latitude || '',
                                            longitude: coordinates?.longitude || ''
                                        },
                                        events: []
                                    });
                                }
                            }

                            // Enrich event with individual details
                            const enrichedEvent = {
                                type: event.type,
                                date: event.date,
                                personId: id, // Phase 11: Utiliser l'ID de la clé Map
                                personDetails: {
                                    name: getName(individual),
                                    surname: getSurname(individual),
                                    gender: getGender(individual),
                                    birthDate: individual.birthDate,
                                    deathDate: individual.deathDate,
                                    birthPlace: individual.fanBirthPlace,
                                    deathPlace: individual.fanDeathPlace,
                                    occupation: individual.occupation
                                }
                            };

                            townUpdates.get(normalizedTownName).events.push(enrichedEvent);
                        });
                    });

                    logger.map.familyTowns.info(`📋 Total: ${individualCount} individus, ${eventsCount} événements`);
                    logger.map.familyTowns.timeEnd('processIndividuals');
                    logger.map.familyTowns.time('applyTownUpdates');

                    // Apply accumulated updates
                    townUpdates.forEach((updateData, normalizedTownName) => {
                        this.addOrUpdateTown(normalizedTownName, updateData.townData);
                        const town = this.townsData.get(normalizedTownName);
                        if (town) {
                            updateData.events.forEach(event => {
                                this.updateTownEvents(town, event);
                            });
                        }
                    });

                    logger.map.familyTowns.timeEnd('applyTownUpdates');
                });

                // logger.map.familyTowns.timeEnd('processAllTownsEvents');
            }
        );

        // Handle cache build completion
        const cacheDisposer = storeEvents.subscribe(
            EVENTS.CACHE.BUILT,
            () => {
                logger.map.familyTowns.info('🔄 Cache built received');
                this.finalizeAllTownsData();

                // Les lieux posés par l'extraction IA (occupation, travel,
                // military…) ne passent pas par processIndividuals (qui ne
                // registre que birth/death/marriage) : un arbre restauré
                // enrichi peut donc référencer des villes absentes du store.
                this._scheduleAiTownsRegistration();
            }
        );

        // Les intégrations IA/Wikipedia en cours de session ajoutent des
        // événements avec des lieux inédits → enregistrement + géocodage
        const aiTownsDisposer = storeEvents.subscribe(
            EVENTS.AI_NOTES.PROCESSING_COMPLETED,
            () => this._scheduleAiTownsRegistration()
        );
        const enrichTownsDisposer = storeEvents.subscribe(
            EVENTS.ENRICHMENT.EVENTS_INTEGRATED,
            () => this._scheduleAiTownsRegistration()
        );

        // Handle import events for map optimization
        const importDisposer = storeEvents.subscribe(
            EVENTS.IMPORT.STARTED,
            ({ type, reason }) => {
                logger.map.familyTowns.debug(`🎯 FamilyTownsStore: Import démarré: type=${type}, reason=${reason}`);
                
                if (type === 'direct' || type === 'cache_restore') {
                    // Pour les imports directs et restaurations cache, différer le traitement map
                    logger.map.familyTowns.debug(`📥 FamilyTownsStore: Import ${type}: traitement map différé`);
                    this.isPendingMapProcessing = true;
                    
                    // Si GoogleMapManager est déjà chargé, lui transmettre l'info
                    if (this.googleMapManager) {
                        this.googleMapManager.setPendingProcessing(true);
                    }
                }
            }
        );

        // Handle cache clearing
        const clearDisposer = storeEvents.subscribe(EVENTS.CACHE.CLEARED, () => {
            logger.map.familyTowns.info('🧹 Clearing town data');
            this.clearAllTowns();
        });

        // Si l'import ou le traitement échoue, lever le drapeau "traitement map
        // différé" — sinon il reste à true et la carte est bloquée jusqu'au
        // prochain import réussi (cf. registerGoogleMapManager / getMapImportState).
        const processErrorDisposer = storeEvents.subscribe(EVENTS.PROCESS.ERROR, () => {
            this._resetPendingMapProcessing('process:error');
        });
        const gedcomErrorDisposer = storeEvents.subscribe(EVENTS.GEDCOM.IMPORT_ERROR, () => {
            this._resetPendingMapProcessing('gedcom:import:error');
        });

        // Store disposers for cleanup
        this.eventDisposers.set('bulkIndividuals', bulkIndividualsDisposer);
        this.eventDisposers.set('cache', cacheDisposer);
        this.eventDisposers.set('aiTowns', aiTownsDisposer);
        this.eventDisposers.set('enrichTowns', enrichTownsDisposer);
        this.eventDisposers.set('import', importDisposer);
        this.eventDisposers.set('clear', clearDisposer);
        this.eventDisposers.set('processError', processErrorDisposer);
        this.eventDisposers.set('gedcomError', gedcomErrorDisposer);
    }

    /**
     * Planifie (débouncé) l'enregistrement + géocodage des lieux d'événements
     * IA/Wikipedia absents du store. Débounce court : une intégration peut
     * émettre plusieurs événements en rafale.
     */
    _scheduleAiTownsRegistration() {
        clearTimeout(this._aiTownsTimer);
        this._aiTownsTimer = setTimeout(() => {
            this._aiTownsTimer = null;
            this.registerAiEventTowns().catch(err => {
                logger.map.familyTowns.warn('⚠️ Enregistrement des lieux IA échoué:', err?.message || err);
            });
        }, 2000);
    }

    /**
     * Parcourt les événements de tous les individus et enregistre les clés de
     * lieu (`l`) absentes du store, puis les géocode via le proxy. Couvre les
     * lieux posés par l'extraction IA (occupation, travel, military…) que
     * processIndividuals ignore (il ne registre que birth/death/marriage).
     * Le géocodage fournit coords + département + libellé propre, ce qui rend
     * ces lieux affichables dans la Chronologie et sur la Carte.
     */
    async registerAiEventTowns() {
        // Import dynamique : évite un cycle avec gedcomDataStore
        const { default: gedcomDataStore } = await import('../../../gedcom/stores/gedcomDataStore.js');
        const individualsCache = gedcomDataStore.individualsCache;
        if (!individualsCache || individualsCache.size === 0) return;

        const missing = new Map();
        individualsCache.forEach((individual) => {
            individual.e?.forEach((event) => {
                const key = event.l;
                if (!key || typeof key !== 'string') return;
                if (normalizeGeoString(key) === 'lieu_inconnu') return;
                // Enregistrer sous la clé BRUTE : c'est elle que la Chronologie
                // cherche (event.l verbatim). Les clés IA récentes sont des
                // slugs propres ; les clés héritées accentuées obtiennent leur
                // propre entrée (géocodée correctement par le proxy).
                if (this.townsData.has(key) || missing.has(key)) return;

                // Libellé lisible depuis le slug ("san-diego" → "San Diego") —
                // remplacé par le libellé officiel au retour du géocodage.
                // country:'' déclenche côté proxy le chemin COMMUNES_FR_KV
                // (gratuit et précis pour les communes françaises).
                const display = key.replace(/-/g, ' ').replace(/(^|\s)\p{L}/gu, (c) => c.toUpperCase());
                missing.set(key, { town: display, townDisplay: display, country: '' });
            });
        });

        if (missing.size > 0) {
            logger.map.familyTowns.info(`🌍 ${missing.size} lieu(x) d'événements IA à enregistrer/géocoder`);
            runInAction(() => {
                missing.forEach((data, key) => this.addOrUpdateTown(key, data));
                this.saveTownsDataToLocalStorage();
            });
        }

        // Géocodage de TOUTES les villes sans coordonnées (nouvelles entrées
        // IA + entrées plus anciennes dont le géocodage avait échoué — le bug
        // `town.name` du proxy a laissé des villes non françaises sans coords
        // pendant des années). Le proxy répond via ses caches KV quand il peut.
        const updates = this._collectTownsNeedingUpdate();
        const candidateKeys = Object.keys(updates);
        if (candidateKeys.length === 0) return;
        await this.updateTownsViaProxy(updates);

        // Bilan lisible : quelles villes ont obtenu des coordonnées
        const resolved = [];
        const unresolved = [];
        candidateKeys.forEach((key) => {
            const town = this.townsData.get(key);
            if (town?.latitude && town?.longitude) {
                resolved.push(`${key} → ${town.town}${town.departement ? ` (${town.departement})` : ''}`);
            } else {
                unresolved.push(key);
            }
        });
        if (resolved.length > 0) {
            logger.map.familyTowns.info(`🌍 Villes géocodées après extraction IA (${resolved.length}) :\n  - ${resolved.join('\n  - ')}`);
        }
        if (unresolved.length > 0) {
            logger.map.familyTowns.info(`🌍 Lieux non géocodables (${unresolved.length}) : ${unresolved.join(', ')}`);
        }
    }

    /**
     * Lève le drapeau de traitement map différé (sur erreur d'import / cache vidé).
     * @param {string} reason - Origine de la réinitialisation (pour le log)
     */
    _resetPendingMapProcessing(reason) {
        if (!this.isPendingMapProcessing) return;
        logger.map.familyTowns.warn(`⚠️ FamilyTownsStore: réinitialisation de l'état pending (${reason})`);
        this.isPendingMapProcessing = false;
        if (this.googleMapManager) {
            this.googleMapManager.setPendingProcessing(false);
        }
    }

    /**
     * Creates marker configuration for a town
     * @param {string} townName - Normalized town name
     * @param {Object} townData - Town data including coordinates
     * @returns {Object|null} Marker configuration or null if invalid data
     */
    createMarkerConfig(townName, townData) {
        // Debug: Log validation details
        // logger.map.familyTowns.debug(`🔍 Creating marker config for "${townName}":`, {
        //    hasLatitude: !!townData?.latitude,
        //    hasLongitude: !!townData?.longitude,
        //    latitude: townData?.latitude,
        //    longitude: townData?.longitude
        //});
        
        if (!townData?.latitude || !townData?.longitude) {
            logger.map.familyTowns.warn(`⚠️ Invalid town data for ${townName}:`, townData);
            return null;
        }

        const config = {
            position: new google.maps.LatLng(
                Number(townData.latitude),
                Number(townData.longitude)
            ),
            options: {
                content: this.createMarkerElement(townData),
                title: townData.townDisplay || townData.town
            }
        };

        this.markerConfigs.set(townName, config);
        return config;
    }

    createMarkerElement(townData) {
        const div = document.createElement('div');
        div.className = 'family-town-marker';
        div.style.cssText = `
            background: #4B5563;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border: 2px solid white;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 12px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        `;
        // townData.events est l'objet de collections { birth:[], death:[], marriage:[]… },
        // PAS un tableau : .length valait donc `undefined` (affiché tel quel dans le
        // marqueur). On somme les longueurs des collections.
        const events = townData.events;
        const eventCount = events
            ? Object.values(events).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)
            : 0;
        div.innerHTML = `<span>${eventCount}</span>`;
        return div;
    }

    // Mettre à jour la méthode getOrCreateMarker pour utiliser le bon nom de couche
    getOrCreateMarker(townName, townData) {
        if (!townData) {
            logger.map.familyTowns.warn(`⚠️ Données manquantes pour la ville ${townName}`);
            return null;
        }

        let config = this.markerConfigs.get(townName);

        if (!config) {
            config = this.createMarkerConfig(townName, townData);
            if (!config) return null;
        }

        return this.markerDisplayManager.addMarker(
            this.markerLayerName,
            townName,
            config.position,
            config.options,
            (marker) => this.handleMarkerClick(marker, townName)
        );
    }

    /**
     * 🗺️ NOUVEAU: Importe les données familiales depuis read-gedcom-geneafan
     * @param {Object} familyTownsData - familyTownsStore généré par read-gedcom-geneafan
     */
    importFamilyTowns(familyTownsData) {
        if (!familyTownsData || typeof familyTownsData !== 'object') {
            logger.map.familyTowns.warn('⚠️ Données familyTownsStore invalides pour import');
            return false;
        }

        logger.map.familyTowns.info(`📍 Import de ${Object.keys(familyTownsData).length} lieux familiaux depuis read-gedcom-geneafan`);

        // 🔑 LOGIQUE CORRIGÉE: Enrichir les villes du GEDCOM avec les données de townsDB
        const existingTowns = this.loadTownsDataFromLocalStorage();
        logger.map.familyTowns.info(`📦 Données disponibles dans townsDB: ${Object.keys(existingTowns).length} villes`);

        runInAction(() => {
            Object.entries(familyTownsData).forEach(([townKey, townData]) => {
                // Chercher si cette ville du GEDCOM a des données enrichies dans townsDB
                const enrichedData = existingTowns[townKey];
                
                if (enrichedData) {
                    // Fusionner les données du GEDCOM avec les données enrichies de townsDB
                    const mergedTownData = {
                        ...townData,  // Données de base du GEDCOM
                        ...enrichedData  // Données enrichies (coordonnées, département) de townsDB
                    };
                    this.addOrUpdateTown(townKey, mergedTownData);
                    // logger.map.familyTowns.debug(`✨ Ville "${townKey}" enrichie depuis townsDB`);
                } else {
                    // Pas de données enrichies, utiliser les données du GEDCOM
                    this.addOrUpdateTown(townKey, townData);
                    // logger.map.familyTowns.debug(`📍 Ville "${townKey}" ajoutée depuis GEDCOM (pas dans townsDB)`);
                }
            });
        });

        logger.map.familyTowns.info(`✅ Import terminé: ${this.townsData.size} lieux du GEDCOM dans familyTownsStore (enrichis avec townsDB)`);
        return true;
    }

    /**
 * Ajoute ou met à jour une ville dans la base de données
 * @param {string} normalizedTownName - Nom normalisé de la ville
 * @param {Object} townData - Données de la ville
 * @param {Object} eventData - Données d'événement associées (optionnel)
 * @returns {boolean} - Succès de l'opération
 */
    addOrUpdateTown(normalizedTownName, townData, eventData = null) {
        // Validation des paramètres essentiels
        if (!normalizedTownName) {
            logger.map.familyTowns.warn('⚠️ Nom de ville normalisé manquant');
            return false;
        }

        if (!townData) {
            logger.map.familyTowns.warn('⚠️ Données de ville manquantes pour', normalizedTownName);
            return false;
        }

        runInAction(() => {
            let town = this.townsData.get(normalizedTownName);

            if (!town) {
                // Validation des coordonnées si présentes
                if (townData.latitude !== undefined && townData.longitude !== undefined) {
                    const lat = Number(townData.latitude);
                    const lng = Number(townData.longitude);

                    if (isNaN(lat) || isNaN(lng) ||
                        lat < -90 || lat > 90 ||
                        lng < -180 || lng > 180) {
                        logger.map.familyTowns.warn(`⚠️ Coordonnées invalides ignorées pour ${normalizedTownName}: (${townData.latitude}, ${townData.longitude})`);
                        townData.latitude = '';
                        townData.longitude = '';
                    }
                }

                // Création des collections d'événements (clés en minuscules = type
                // d'événement applicatif ; les anciennes clés GEDCOM majuscules
                // BIRT/DEAT/… n'étaient jamais lues ni écrites — supprimées).
                const eventTypes = {
                    birth: observable([]),
                    death: observable([]),
                    marriage: observable([]),
                    burial: observable([]),
                    occupation: observable([]),
                    event: observable([])
                };

                // Création d'une nouvelle ville avec des valeurs par défaut pour les champs manquants
                town = observable({
                    town: townData.town || '',
                    townDisplay: townData.townDisplay || townData.town || '',
                    departement: townData.departement || '',
                    departementColor: townData.departementColor || '',
                    country: townData.country || '',
                    countryCode: townData.countryCode || '',
                    countryColor: townData.countryColor || '',
                    latitude: townData.latitude || '',
                    longitude: townData.longitude || '',
                    events: eventTypes,
                    statistics: TownStatisticsManager.createEmptyStatistics()
                });

                this.townsData.set(normalizedTownName, town);
            } else {
                // Mise à jour d'une ville existante
                // Vérifier si events existe et est observable
                if (!town.events || !isObservable(town.events)) {
                    town.events = observable({
                        birth: observable([]),
                        death: observable([]),
                        marriage: observable([]),
                        burial: observable([]),
                        occupation: observable([]),
                        event: observable([])
                    });
                }

                // Validation des coordonnées mises à jour si présentes
                if (townData.latitude !== undefined && townData.longitude !== undefined) {
                    const lat = Number(townData.latitude);
                    const lng = Number(townData.longitude);

                    if (isNaN(lat) || isNaN(lng) ||
                        lat < -90 || lat > 90 ||
                        lng < -180 || lng > 180) {
                        logger.map.familyTowns.warn(`⚠️ Coordonnées de mise à jour invalides ignorées pour ${normalizedTownName}: (${townData.latitude}, ${townData.longitude})`);
                        delete townData.latitude;
                        delete townData.longitude;
                    }
                }

                // Mise à jour des champs avec vérification de validité
                Object.entries(townData).forEach(([field, value]) => {
                    if (value !== undefined && value !== null && field !== 'events') {
                        // 🔑 PROTECTION: Ne pas écraser les coordonnées/départements existants avec des valeurs vides
                        if (['latitude', 'longitude', 'departement', 'country'].includes(field)) {
                            // Seulement mettre à jour si la nouvelle valeur n'est pas vide ET (pas de valeur existante OU nouvelle valeur non vide)
                            if (value && value.toString().trim() !== '' && (!town[field] || town[field].toString().trim() === '')) {
                                town[field] = value;
                            }
                        } else {
                            // Pour tous les autres champs, mise à jour normale
                            town[field] = value;
                        }
                    }
                });
            }

            // Traiter les données d'événement si fournies
            if (eventData) {
                if (!this.isValidEventData(eventData)) {
                    logger.map.familyTowns.warn('⚠️ Données d\'événement invalides pour', normalizedTownName, eventData);
                } else {
                    this.invalidateCache(normalizedTownName);
                    this.updateTownEvents(town, eventData);
                }
            }
        });

        return true;
    }

    /**
     * Vérifie si les données d'événement sont valides
     * @param {Object} eventData - Données d'événement à valider
     * @returns {boolean} - Validité des données
     */
    isValidEventData(eventData) {
        // L'événement doit avoir un type
        if (!eventData || !eventData.type) {
            return false;
        }

        // Le type doit être valide
        const validEventTypes = ['birth', 'death', 'marriage', 'burial', 'occupation', 'event'];
        if (!validEventTypes.includes(eventData.type)) {
            return false;
        }

        // Vérification des détails de personne
        if (!eventData.personId || !eventData.personDetails) {
            return false;
        }

        return true;
    }

    updateExistingTownData(town, updates) {
        Object.entries(updates).forEach(([field, value]) => {
            if (value !== undefined && value !== null) {
                town[field] = value;
            }
        });
    }

    // Cache Management
    invalidateCache(normalizedTownName) {
        this.infoWindowContentCache.delete(normalizedTownName);
        this.markerConfigs.delete(normalizedTownName);
    }

    clearAllCaches() {
        this.markerConfigs.clear();
        this.infoWindowContentCache.clear();
    }

    // Marker Management
    createMarkerContent(color = '#4B5563') {
        const element = document.createElement('div');
        element.className = 'town-marker';
        element.style.cssText = `
            background: ${color};
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border: 2px solid white;
        `;
        return element;
    }

    updateMarkers() {
        logger.map.familyTowns.debug(`🔍 updateMarkers called - townsData size: ${this.townsData.size}, hasMap: ${!!this.map}`);
        
        if (!this.map) {
            logger.map.familyTowns.warn('⚠️ updateMarkers: No map available');
            return;
        }

        if (!this.markerDisplayManager.isInitialized()) {
            logger.map.familyTowns.debug('🔧 Initializing cluster for marker display');
            this.markerDisplayManager.initializeCluster(this.map, this.createClusterMarker);
        }

        if (this.townsData.size === 0) {
            logger.map.familyTowns.warn('⚠️ updateMarkers: No towns data available');
            return;
        }

        let markersCreated = 0;
        this.townsData.forEach((townData, townName) => {
            // logger.map.familyTowns.debug(`🏗️ Processing marker for "${townName}":`, townData);
            const marker = this.getOrCreateMarker(townName, townData);
            if (marker) markersCreated++;
        });
        
        logger.map.familyTowns.debug(`✅ updateMarkers completed: ${markersCreated} markers processed out of ${this.townsData.size} towns`);
    }

    // Surcharge de la méthode toggleVisibility de BaseLayerStore pour maintenir
    // la compatibilité avec le code existant
    toggleVisibility(visible) {
        super.toggleVisibility(visible);
    }

    /**
 * Hook: Actions après affichage du calque
 * Centre automatiquement la carte sur les marqueurs de villes familiales
 * Simplifié pour correspondre à l'approche de rootAncestorTownsStore
 */
    afterLayerShown() {
        logger.map.familyTowns.debug('🔄 Calque des villes familiales affiché, centrage automatique');

        // Forcer un événement de redimensionnement
        if (this.map) {
            google.maps.event.trigger(this.map, 'resize');
        }

        // Vérifier qu'il y a des marqueurs avant de tenter le centrage
        // Utiliser la même logique simple que rootAncestorTownsStore
        if (this.townsData && this.townsData.size > 0) {
            logger.map.familyTowns.debug(`📊 Centrage sur ${this.townsData.size} villes familiales`);

            // Utiliser la méthode générique standardisée
            this.centerMapOnFamilyMarkers();
        } else {
            logger.map.familyTowns.debug('⚠️ Pas de données de villes familiales pour le centrage');
        }
    }

    /**
     * Hook (BaseLayerStore.applyVisibility) : préparation avant affichage.
     * Redimensionne la carte et force le recalcul des bounds — initializeMapBounds
     * sort tôt si calculatedBounds est déjà défini, donc on le réinitialise ici.
     * (Remplace l'ancienne surcharge complète d'applyVisibility, qui dupliquait
     * ~70 lignes de la classe de base : c'est désormais la base qui orchestre,
     * via les hooks, l'init cluster / le délai / le centrage différé / initializeMapBounds.)
     */
    prepareLayerBeforeShow() {
        if (this.map) google.maps.event.trigger(this.map, 'resize');
        this.calculatedBounds = null;
        this.hasTooManyMarkers = false;
    }

    /**
     * Hook (BaseLayerStore.applyVisibility) : (re)construit les marqueurs du calque.
     */
    updateLayerMarkers() {
        this.updateMarkers();
    }

    // Stats and Data Management
    recalculateAllTownsStatistics() {
        runInAction(() => {
            logger.map.familyTowns.info('🏘️ Début du recalcul des statistiques');
            
            let processedTowns = 0;
            let skippedTowns = 0;

            let totalEvents = {
                births: 0,
                deaths: 0,
                marriages: 0
            };

            this.townsData.forEach((town, normalizedTownName) => {
                try {
                    town.statistics = TownStatisticsManager.createEmptyStatistics();
                    
                    // Skip towns that don't have events (from cache but not in current GEDCOM)
                    if (!town.events) {
                        skippedTowns++;
                        return;
                    }
                    
                    processedTowns++;
                    
                    ['birth', 'death', 'marriage'].forEach(eventType => {
                        if (!Array.isArray(town.events[eventType])) {
                            return; // Skip this event type if not present
                        }

                        const eventCount = town.events[eventType].length;
                        totalEvents[eventType + 's'] += eventCount;

                        town.events[eventType].forEach(event => {
                            if (event) TownStatisticsManager.updateTownStatistics(town, event);
                        });

                        // logger.map.familyTowns.debug(`📊 ${normalizedTownName}:`,
                        //    JSON.stringify({
                        //        events: {
                        //            births: town.events.birth?.length || 0,
                        //            deaths: town.events.death?.length || 0,
                        //            marriages: town.events.marriage?.length || 0
                        //        },
                        //        stats: toJS(town.statistics)
                        //    }, null, 2)
                        // );
                    });
                } catch (error) {
                    logger.map.familyTowns.error(`Erreur: ${normalizedTownName}:`, error);
                }
            });

            logger.map.familyTowns.info('📊 Total des événements:', totalEvents);
            logger.map.familyTowns.info(`📊 Villes traitées: ${processedTowns}, Villes ignorées (cache): ${skippedTowns}`);
        });
    }

    finalizeAllTownsData() {
        logger.map.familyTowns.info('🏁 Début finalizeAllTownsData');
        this.recalculateAllTownsStatistics();
        this.clearAllCaches();
        logger.map.familyTowns.info('✅ Fin finalizeAllTownsData');

        // Mettre à jour les marqueurs après la finalisation des données
        if (this.map) {
            logger.map.familyTowns.debug('📍 Mise à jour des marqueurs après finalisation');
            this.updateMarkers();

            // Réinitialiser et recalculer les bounds pour le centrage optimisé
            this.calculatedBounds = null;
            this.hasTooManyMarkers = false;
            this.lastContainerDimensions = null; // Réinitialiser aussi les dimensions mémorisées
            this.initializeMapBounds();
        }

        this.saveTownsDataToLocalStorage();
    }

    // hasActiveMarkers() est hérité de BaseLayerStore.

    /**
     * Initialise les limites géographiques du calque pour optimiser le centrage
     * Cette méthode n'est appelée qu'une fois après le chargement complet des données
     */
    initializeMapBounds() {
        if (!this.markerDisplayManager?.layers?.get(this.markerLayerName) || this.calculatedBounds) {
            return; // Bounds déjà calculés ou pas de marqueurs
        }

        logger.map.familyTowns.debug('🗺️ Calcul des limites géographiques pour le calque familial');

        const markers = [];
        this.markerDisplayManager.layers.get(this.markerLayerName).forEach(marker => {
            if (marker && marker.position) {
                markers.push(marker);
            }
        });

        if (markers.length === 0) {
            logger.map.familyTowns.debug('⚠️ Aucun marqueur pour calculer les limites');
            return;
        }

        const bounds = new google.maps.LatLngBounds();
        markers.forEach(marker => bounds.extend(marker.position));

        // Vérifier si les limites sont trop larges
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const spanLat = Math.abs(ne.lat() - sw.lat());
        const spanLng = Math.abs(ne.lng() - sw.lng());

        this.hasTooManyMarkers = (spanLat > 60 || spanLng > 60);
        this.calculatedBounds = bounds;

        logger.map.familyTowns.debug(`✅ Limites calculées pour ${markers.length} marqueurs familiaux (trop étendu: ${this.hasTooManyMarkers})`);
    }

    /**
     * Centre la carte sur les marqueurs des villes familiales
     * Utilise la méthode générique de BaseLayerStore pour un comportement cohérent
     */
    centerMapOnFamilyMarkers() {
        // Utiliser la méthode générique avec options adaptées aux villes familiales
        this.centerMapOnMarkers({
            forceResize: true  // Utile pour les changements de calque
        });
    }

    /**
     * Décompresse un événement compressé (Phase 6 Cloud) vers le format standard
     * @param {Object} compressedEvent - Événement compressé avec t, d, l
     * @returns {Object|null} - Événement décompressé avec type, date, town
     */
    decompressEvent(compressedEvent) {
        if (!compressedEvent || !compressedEvent.t) return null;
        
        // Source de vérité unique : dictionnaire unifié du parser.
        // (Remplace une copie locale partielle ; le filtrage par type
        // pertinent pour la carte est fait par l'appelant via validEventTypes.)
        const type = UNIFIED_EVENT_TYPE_DECOMPRESSION[compressedEvent.t];
        if (!type) return null;
        
        // Décompresser la date si elle existe
        let date = null;
        if (compressedEvent.d) {
            const dateStr = compressedEvent.d.toString();
            if (dateStr.length === 8) {
                // Format YYYYMMDD -> DD/MM/YYYY
                const year = dateStr.substring(0, 4);
                const month = dateStr.substring(4, 6);
                const day = dateStr.substring(6, 8);
                date = `${day}/${month}/${year}`;
            }
        }
        
        return {
            type: type,
            date: date,
            town: compressedEvent.l || null // l = location key
        };
    }

    /**
     * Assure le bon centrage de la carte après restauration du cache
     * Réinitialise les bounds calculés pour forcer un nouveau calcul
     * Et déclenche la mise à jour des stores dépendants
     */
    ensureMapCenteringAfterRestore() {
        // Réinitialiser les bounds calculés pour forcer un nouveau calcul
        this.calculatedBounds = null;
        this.hasTooManyMarkers = false;
        this.lastContainerDimensions = null;
        
        logger.map.familyTowns.debug('🗺️ Bounds réinitialisés pour centrage post-cache');
        
        // Réinitialiser les bounds si on a une carte
        if (this.map) {
            this.initializeMapBounds();
        }
        
        // La mise à jour des patronymes est maintenant gérée après CACHE.BUILT
    }
    
    /**
     * Déclenche la mise à jour des stores dépendants après restauration de cache
     */
    async triggerSurnamesUpdate() {
        try {
            // Import dynamique pour éviter les dépendances circulaires
            const { default: surnamesTownsStore } = await import('./surnamesTownsStore.js');
            
            if (surnamesTownsStore && typeof surnamesTownsStore.updateSurnamesList === 'function') {
                logger.map.familyTowns.debug('📋 Déclenchement manuel de updateSurnamesList après cache');
                surnamesTownsStore.updateSurnamesList();
            } else {
                logger.map.familyTowns.warn('⚠️ surnamesTownsStore.updateSurnamesList non disponible');
            }
        } catch (error) {
            logger.map.familyTowns.warn('⚠️ Erreur lors de la mise à jour des patronymes:', error);
        }
    }

    cleanData(data) {
        return JSON.parse(JSON.stringify(data));
    }

    // Storage Management
    loadTownsDataFromLocalStorage = () => {
        try {
            const stored = localStorage.getItem('townsDB');
            if (!stored) {
                logger.map.familyTowns.debug('📦 loadTownsDataFromLocalStorage - townsDB content: empty');
                return {};
            }

            // Parse une seule fois (auparavant parsé deux fois : une pour le log, une pour la logique).
            const parsed = JSON.parse(stored);
            logger.map.familyTowns.debug(`📦 loadTownsDataFromLocalStorage - townsDB content: ${Object.keys(parsed).length} towns`);
            const validTowns = {};
            const invalidKeys = [];

            Object.entries(parsed).forEach(([key, townData]) => {
                if (!this._isValidTownData(townData)) {
                    invalidKeys.push(key);
                    return;
                }
                validTowns[key] = townData;
            });

            if (invalidKeys.length > 0) {
                const newCache = { ...parsed };
                invalidKeys.forEach(key => delete newCache[key]);
                localStorage.setItem('townsDB', JSON.stringify(newCache));
            }

            logger.map.familyTowns.debug('📦 loadTownsDataFromLocalStorage - validTowns loaded:', Object.keys(validTowns).length);
            // NE PAS charger automatiquement dans le store
            // this.setTownsData(validTowns);
            return validTowns;
        } catch (error) {
            logger.map.familyTowns.error('Erreur lors du chargement depuis localStorage:', error);
            // Ne pas toucher au store, juste retourner un objet vide
            return {};
        }
    };

    _isValidTownData(townData) {
        return townData &&
            townData.town &&
            typeof townData.town === 'string' &&
            townData.latitude &&
            !isNaN(townData.latitude) &&
            townData.longitude &&
            !isNaN(townData.longitude);
    }

    getStorageData() {
        const result = {};
        this.townsData.forEach((townData, key) => {
            result[key] = {
                town: townData.town,
                townDisplay: townData.townDisplay,
                departement: townData.departement,
                departementColor: townData.departementColor,
                country: townData.country,
                countryCode: townData.countryCode,
                countryColor: townData.countryColor,
                latitude: townData.latitude,
                longitude: townData.longitude
            };
        });
        return result;
    }

    saveTownsDataToLocalStorage() {
        try {
            const storageData = {};
            let savedCount = 0;
            let skippedCount = 0;
            const skippedTowns = []; // Collecter les noms pour un log résumé

            this.townsData.forEach((townData, key) => {
                // 🔑 VALIDATION: Ne sauvegarder que les villes avec coordonnées valides
                const hasValidCoords = townData.latitude &&
                                     townData.longitude &&
                                     townData.latitude.toString().trim() !== '' &&
                                     townData.longitude.toString().trim() !== '';

                if (hasValidCoords) {
                    storageData[key] = {
                        town: townData.town,
                        townDisplay: townData.townDisplay,
                        departement: townData.departement,
                        departementColor: townData.departementColor,
                        country: townData.country,
                        countryCode: townData.countryCode,
                        countryColor: townData.countryColor,
                        latitude: townData.latitude,
                        longitude: townData.longitude
                    };
                    savedCount++;
                } else {
                    skippedTowns.push(key);
                    skippedCount++;
                }
            });

            localStorage.setItem('townsDB', JSON.stringify(storageData));
            logger.map.familyTowns.info(`💾 Sauvegarde localStorage: ${savedCount} villes sauvées, ${skippedCount} ignorées`);

            // Log résumé des villes ignorées (seulement si > 0 et en debug)
            if (skippedCount > 0) {
                const preview = skippedTowns.slice(0, 5).join(', ');
                const more = skippedCount > 5 ? ` ... et ${skippedCount - 5} autres` : '';
                logger.map.familyTowns.debug(`🚫 Villes sans coordonnées: ${preview}${more}`);
            }
        } catch (error) {
            logger.map.familyTowns.error('Error saving to localStorage:', error);
        }
    }

    // Proxy Update Management
    async updateTownsViaProxy(townsToUpdate = null) {
        if (this.isLoading) {
            logger.map.familyTowns.warn("⚠️ Une mise à jour est déjà en cours");
            return;
        }

        try {
            logger.map.familyTowns.debug('🔄 Début de la mise à jour via proxy');
            this.setIsLoading(true);

            logger.map.familyTowns.debug('📣 Émission de UPDATE_START');
            storeEvents.emit(EVENTS.TOWN.UPDATE_START);

            const updates = townsToUpdate || this._collectTownsNeedingUpdate();

            if (Object.keys(updates).length === 0) {
                logger.map.familyTowns.debug('ℹ️ Aucune ville à mettre à jour');
                logger.map.familyTowns.debug('📣 Émission de UPDATE_COMPLETE');
                storeEvents.emit(EVENTS.TOWN.UPDATE_COMPLETE);
                return;
            }

            // Chunk the request: the geocode worker caps towns/OpenCage-calls per
            // request, so sending the whole set at once gets rejected wholesale on
            // large trees (→ 0 towns geocoded). Send batches sequentially, keep
            // partial results, and tolerate a failed batch.
            const allKeys = Object.keys(updates);
            const CHUNK_SIZE = 400;
            const REQUEST_TIMEOUT_MS = 20000;
            logger.map.familyTowns.debug(`🔄 Mise à jour de ${allKeys.length} villes (lots de ${CHUNK_SIZE})`);

            const updatedTowns = {};
            let lastError = null;
            for (let i = 0; i < allKeys.length; i += CHUNK_SIZE) {
                const batchKeys = allKeys.slice(i, i + CHUNK_SIZE);
                const familyTowns = {};
                for (const k of batchKeys) familyTowns[k] = updates[k];

                const controller = new AbortController();
                const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
                try {
                    const response = await fetch('https://geocode.genealogie.app', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ familyTowns }),
                        signal: controller.signal,
                    });
                    if (!response.ok) {
                        let msg = `HTTP ${response.status}`;
                        try { const e = await response.json(); msg = e.message || msg; } catch (_) { /* */ }
                        lastError = new Error(msg);
                        logger.map.familyTowns.warn(`🚫 Lot ${i / CHUNK_SIZE} échoué: ${msg}`);
                        continue; // garder les autres lots
                    }
                    const result = await response.json();
                    const batchTowns = result.towns || result;
                    const stats = result.stats;
                    if (stats) {
                        logger.map.familyTowns.info(`📊 Géocodage stats lot: OPENCAGE=${stats.OPENCAGE}, KV=${stats.TOWNS_KV}+${stats.COMMUNES_FR_KV}`);
                    }
                    Object.assign(updatedTowns, batchTowns);
                } catch (err) {
                    lastError = err;
                    logger.map.familyTowns.warn(`🚫 Lot ${i / CHUNK_SIZE} en erreur (timeout/réseau):`, err?.message || err);
                } finally {
                    clearTimeout(timer);
                }
            }

            logger.map.familyTowns.debug('✅ Villes géocodées (cumul):', Object.keys(updatedTowns).length);

            // Apply all batches and save the localStorage DB ONCE (not per town).
            runInAction(() => {
                Object.entries(updatedTowns).forEach(([key, data]) => {
                    if (data.latitude && data.longitude) {
                        this.updateTown(key, data, { skipSave: true });
                    }
                });
                this.saveTownsDataToLocalStorage();
            });

            // If every batch failed and nothing was geocoded, surface the error.
            if (Object.keys(updatedTowns).length === 0 && lastError) throw lastError;

            logger.map.familyTowns.debug('📣 Émission de UPDATE_COMPLETE');
            storeEvents.emit(EVENTS.TOWN.UPDATE_COMPLETE);

        } catch (error) {
            logger.map.familyTowns.error('❌ Erreur lors de la mise à jour:', error);
            logger.map.familyTowns.debug('📣 Émission de UPDATE_ERROR');
            storeEvents.emit(EVENTS.TOWN.UPDATE_ERROR, error);
            throw error;
        } finally {
            this.setIsLoading(false);
            logger.map.familyTowns.debug('✅ Fin de updateTownsViaProxy');
        }
    }

    // Nouvelle méthode auxiliaire pour identifier les villes à mettre à jour
    _collectTownsNeedingUpdate() {
        const updates = {};
        let debugCount = 0;
        this.townsData.forEach((town, key) => {
            // Ne géocoder que les villes sans coordonnées
            // Les départements et pays peuvent être enrichis plus tard si nécessaire
            const needsUpdate = !town.latitude || !town.longitude;
            if (needsUpdate) {
                updates[key] = this.cleanData(town);
                
                // Debug pour les 5 premiers cas
                if (debugCount < 5) {
                    logger.map.familyTowns.debug(`🔍 Ville "${key}" nécessite géocodage:`, {
                        hasLat: !!town.latitude,
                        hasLng: !!town.longitude,
                        hasDept: !!town.departement,
                        hasCountry: !!town.country,
                        town: town.town
                    });
                    debugCount++;
                }
            }
        });
        return updates;
    }

    /**
     * Surcharge de la méthode cleanup de BaseLayerStore
     * Nettoyage des ressources spécifiques à ce calque
     */
    cleanup() {
        this.clearAllCaches();
        super.cleanup(); // disposes the base Set (LAYERS.CHANGED subscription)
        this.eventDisposers.forEach(disposer => disposer());
        this.eventDisposers.clear();
    }

    clearAllTowns() {
        runInAction(() => {
            logger.map.familyTowns.debug('🧹 clearAllTowns - AVANT:');
            logger.map.familyTowns.debug('  - townsData size:', this.townsData.size);
            logger.map.familyTowns.debug('  - localStorage townsDB:', localStorage.getItem('townsDB') ? `${Object.keys(JSON.parse(localStorage.getItem('townsDB'))).length} entries` : 'empty');
            
            this.townsData.clear();
            this.eventsData.clear();
            this.clearAllCaches();

            // Réinitialiser aussi les bounds calculés
            this.calculatedBounds = null;
            this.hasTooManyMarkers = false;

            // Lever le drapeau de traitement différé : sinon, après un CACHE.CLEARED
            // la carte resterait bloquée en attente d'un traitement qui n'arrivera pas.
            this.isPendingMapProcessing = false;
            if (this.googleMapManager) {
                this.googleMapManager.setPendingProcessing(false);
            }

            if (this.markerDisplayManager) {
                this.markerDisplayManager.clearMarkers();
            }
            
            logger.map.familyTowns.debug('🧹 clearAllTowns - APRÈS:');
            logger.map.familyTowns.debug('  - townsData size:', this.townsData.size);
            logger.map.familyTowns.debug('  - localStorage townsDB:', localStorage.getItem('townsDB') ? `${Object.keys(JSON.parse(localStorage.getItem('townsDB'))).length} entries` : 'empty');
        });
    }

    // Getters and Setters
    setIsLoading(loading) {
        this.isLoading = loading;
    }

    setTownsData(towns) {
        runInAction(() => {
            // replace() conserve l'ObservableMap (et donc ses observateurs) ;
            // réaffecter une new Map brute casserait silencieusement les reactions.
            this.townsData.replace(Object.entries(towns));
        });
    }

    /**
 * Met à jour les données d'une ville existante
 * @param {string} key - Clé de la ville
 * @param {Object} updates - Mises à jour à appliquer
 * @returns {boolean} - Succès de la mise à jour
 */
    updateTown(key, updates, options = {}) {
        // Validation des paramètres
        if (!key) {
            logger.map.familyTowns.warn('⚠️ Clé de ville manquante pour updateTown');
            return false;
        }

        if (!updates || typeof updates !== 'object') {
            logger.map.familyTowns.warn(`⚠️ Mises à jour invalides pour la ville ${key}`);
            return false;
        }

        let success = false;
        runInAction(() => {
            const town = this.townsData.get(key);
            if (town) {
                // Validation des coordonnées si elles sont mises à jour
                if (updates.latitude !== undefined || updates.longitude !== undefined) {
                    const newLat = updates.latitude !== undefined ? updates.latitude : town.latitude;
                    const newLng = updates.longitude !== undefined ? updates.longitude : town.longitude;

                    if (isNaN(Number(newLat)) || isNaN(Number(newLng)) ||
                        Number(newLat) < -90 || Number(newLat) > 90 ||
                        Number(newLng) < -180 || Number(newLng) > 180) {
                        logger.map.familyTowns.warn(`⚠️ Coordonnées invalides pour ${key}: (${newLat}, ${newLng})`);
                        // Continuer la mise à jour mais ignorer les coordonnées invalides
                        delete updates.latitude;
                        delete updates.longitude;
                    }
                }

                // Mise à jour des propriétés avec vérification
                Object.entries(updates).forEach(([field, value]) => {
                    if (value !== undefined && value !== null) {
                        town[field] = value;
                    }
                });

                // Mise à jour du stockage si nécessaire. `skipSave` permet aux
                // boucles (géocodage par lots) de sauvegarder une seule fois à la fin
                // au lieu de sérialiser tout le DB à chaque ville.
                if (!options.skipSave && (updates.latitude || updates.longitude ||
                    updates.departement || updates.country)) {
                    this.saveTownsDataToLocalStorage();
                }

                success = true;
            } else {
                logger.map.familyTowns.warn(`⚠️ Tentative de mise à jour d'une ville inexistante: ${key}`);
            }
        });

        return success;
    }

    getTown(key) {
        const town = this.townsData.get(key);
        return town ? this.cleanData(town) : null;
    }

    getAllTowns() {
        const result = {};
        this.townsData.forEach((townData, key) => {
            result[key] = {
                town: townData.town,
                townDisplay: townData.townDisplay,
                departement: townData.departement,
                departementColor: townData.departementColor,
                country: townData.country,
                countryColor: townData.countryColor,
                latitude: townData.latitude,
                longitude: townData.longitude,
                events: toJS(townData.events)  // Ajout des événements
            };
        });
        return result;
    }

    // Méthode pour accéder aux données des villes
    /**
     * Libellé d'affichage d'une ville : "Toulon (Var)" pour la France,
     * "Alger (Algérie)" hors de France, sans doublon ("Paris" plutôt que
     * "Paris (Paris)"). Le pays vient du champ country ou, à défaut, des
     * components OpenCage stockés au géocodage.
     */
    formatTownDisplay(townData) {
        if (!townData?.town) return '';
        const dep = townData.departement;
        if (dep && dep !== townData.town) {
            return `${townData.town} (${dep})`;
        }
        const country = townData.country || townData.components?.country || '';
        if (country && country !== 'France') {
            return `${townData.town} (${country})`;
        }
        return townData.town;
    }

    getTownsData(townKey) {
        // Utilisation directe de townsData, pas besoin de cache redondant
        if (townKey) {
            return this.townsData.get(townKey);
        }
        // Retourner toutes les données sous forme d'objet
        return Object.fromEntries(this.townsData);
    }

    get totalTowns() {
        return this.townsData.size;
    }

    // Map Initialization and Management
    initialize(map) {
        logger.map.familyTowns.debug('🎯 Initialisation de FamilyTownsStore');
        this.map = map;
        this.markerDisplayManager.initializeCluster(map, this.createClusterMarker);

        if (!this.markerDisplayManager.isInitialized()) {
            logger.map.familyTowns.warn('❌ Échec de l\'initialisation du clustering');
            return;
        }

        // Phase 4 Fix: Déclencher updateMarkers après l'initialisation de la carte
        if (this.townsData.size > 0) {
            logger.map.familyTowns.debug('🔧 Phase 4 Fix: Triggering updateMarkers after map initialization');
            this.updateMarkers();
        }
    }

    createClusterMarker({ count, position }) {
        return createClusterBadge({ count, position, color: '#4B5563', className: 'family-cluster-marker' });
    }

    // InfoWindow Management
    handleMarkerClick(marker, normalizedTownName) {
        const townData = this.townsData.get(normalizedTownName);
        if (!townData) return;

        let content = this.infoWindowContentCache.get(normalizedTownName);
        if (!content) {
            content = infoWindowContentManager.createInfoWindowContent(
                townData.townDisplay || townData.town,
                townData
            );
            this.infoWindowContentCache.set(normalizedTownName, content);
        }

        infoWindowDisplayManager.showInfoWindow(marker, content);
    }

    /**
     * Processes and adds/updates events for a town
     * Handles event deduplication and standardization
     * @param {Object} town - Town object
     * @param {Object} eventData - Event data to process
     */
    updateTownEvents(town, eventData) {
        if (!eventData?.type || !town?.events) {
            logger.map.familyTowns.debug('❌ Invalid event or town structure:', { eventData, town });
            return;
        }

        // Standardize event types to lowercase only
        const validEventTypes = ['birth', 'death', 'marriage', 'burial', 'occupation', 'event'];

        if (!validEventTypes.includes(eventData.type)) {
            logger.map.familyTowns.debug('❌ Unrecognized event type:', eventData.type);
            return;
        }

        const enrichedEvent = {
            ...eventData,
            personDetails: { ...eventData.personDetails }
        };

        // Add or update event in standardized type
        if (!Array.isArray(town.events[eventData.type])) {
            town.events[eventData.type] = observable([]);
        }

        const existingIndex = town.events[eventData.type].findIndex(
            e => e.personId === enrichedEvent.personId && e.date === enrichedEvent.date
        );

        if (existingIndex !== -1) {
            town.events[eventData.type][existingIndex] = enrichedEvent;
        } else {
            town.events[eventData.type].push(enrichedEvent);
        }
    }

    /**
     * Méthodes pour l'optimisation du contexte d'import de la Family Map
     */
    
    /**
     * Enregistre GoogleMapManager et transmet l'état d'import en attente
     */
    registerGoogleMapManager(googleMapManager) {
        logger.map.familyTowns.debug('🔗 FamilyTownsStore: Enregistrement de GoogleMapManager');
        this.googleMapManager = googleMapManager;
        
        // Transmettre l'état d'import en cours s'il y en a un
        if (this.isPendingMapProcessing) {
            logger.map.familyTowns.debug('📤 FamilyTownsStore: Transmission de l\'état pending vers GoogleMapManager');
            googleMapManager.setPendingProcessing(true);
        }
    }
    
    /**
     * Obtient l'état du contexte d'import pour GoogleMapManager
     */
    getMapImportState() {
        return {
            isPendingProcessing: this.isPendingMapProcessing
        };
    }
    
    /**
     * Réinitialise l'état d'import (appelé par GoogleMapManager)
     */
    clearMapImportState() {
        logger.map.familyTowns.debug('🧹 FamilyTownsStore: Nettoyage de l\'état d\'import');
        this.isPendingMapProcessing = false;
    }
}

// Export singleton instance
export default new FamilyTownsStore();